import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useRef, memo } from 'react';
import { List } from 'immutable';
import { connect } from 'react-redux';

import connectOptions, { mergeProps } from '../../utils/connectOptions';
import Popups from '../../components/Popup';
import { save, changeLabel } from '../../actions/project/industryPopup';
import { showInformation } from '../../utils/MessagePopup';
import { usePopupContext } from '../../contexts/PopupContext';

/**
 * Popup for creating a new industry.
 *
 * @param props {Object}.
 * @param props.industryLabel {String} Industry label.
 * @param props.error {List} Error list.
 */
const NewIndustryPopupContainer = props => {
  const { closePopup, openPopup } = usePopupContext();
  const { children, popup, status, afterClose, save, changeLabel, ...rest } = props;

  useEffect(() => {
    const initialText = popup.getIn(['props', 'initialText'], '');

    if (initialText) {
      changeLabel(initialText);
    }
  }, []);

  const prevStatus = useRef(status);

  useEffect(() => {
    if (prevStatus.current !== status && status === 'saved') {
      closePopup();
      showInformation(openPopup, 'Saved Successfully!');
    }
    prevStatus.current = status;
  }, [status, closePopup, openPopup]);

  const handleCreate = useCallback(() => {
    save({ afterSuccess: afterClose });
  }, [save, afterClose]);

  const handleClose = useCallback(() => {
    const closeWithoutSave = popup.getIn(['props', 'closeWithoutSave']);

    if (closeWithoutSave) {
      closeWithoutSave();
    }
    closePopup();
  }, [popup, closePopup]);

  const handleChange = useCallback(
    event => {
      changeLabel(event.target.value);
    },
    [changeLabel],
  );

  return (
    <div>
      <Popups.NewIndustryPopup
        {...rest}
        onChange={handleChange}
        onClose={handleClose}
        onCreate={handleCreate}
        status={status}
      />
      {children}
    </div>
  );
};

NewIndustryPopupContainer.propTypes = {
  errors: PropTypes.instanceOf(List).isRequired,
  industryLabel: PropTypes.string.isRequired,
  status: PropTypes.string.isRequired,
};

const mapStateToProps = (state, ownProps) => {
  const popupState = state.project.industryPopup;

  return {
    industryLabel: popupState.get('industryLabel'),
    status: popupState.get('status'),
    errors: popupState.get('errors'),
    afterClose: ownProps.popup.getIn(['props', 'afterClose']),
    showInformation,
  };
};

export default connect(
  mapStateToProps,
  {
    save,
    changeLabel,
  },
  mergeProps,
  connectOptions,
)(memo(NewIndustryPopupContainer));
